import React, { memo, useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Redirect, useLocation } from "react-router-dom";
import { SelectionPax } from "../selection-guests/selection-pax";
import { SelectionCalendar } from "../selection-calendar/selection-calendar";
import { SelectionTime } from "../selection-time/selection-time";
import { Filters, FiltersPosition } from "../../filters/filters";
import { selectionStore } from "../../../stores/selection.store";
import { ErrorMessage } from "../../errors/message-error-view";
import { ApiErrorCode } from "../../../services/api-errors";
import { selectionStoreChangesService } from "../../../services/selection-store-changes.service";
import { GolEventListener } from "../../calendar/calendar";
import { ContinueButton } from "../../continue-button/continue-button";
import { buildRouteWithParams } from "../../../helpers/build-route-with-params";
import { config } from "../../../config";
import { useErrorHandler } from "react-error-boundary";
import {
  cancelPendingBookingRequest,
  createInitialPendingBookingRequest,
} from "../../../requests/pending-booking.requests";
import { CommercialKeys, Menu, PendingBooking } from "../../../types";
import { moduleSettings } from "../../../stores/module-settings.store";
import { pendingBookingStore } from "../../../stores/pending-booking.store";
import { commercialsStore } from "../../../stores/commercials.store";
import { Loader } from "../../loader/loader";
import { requestParamsImpl } from "../../../services/request-params.service";
import { RestaurantGroupSuggestions } from "../../restaurant-group-suggestions/restaurant-group-suggestions";

const SelectionCriteriaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin-bottom: 32px;
`;

const SelectionCriteriaLayout = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;

  @media screen and (max-width: 1010px) {
    flex-direction: column;
    align-items: center;
    #selection-pax,
    #selection-date {
      margin-top: 8px;
    }
    #selection-time {
      margin-top: 12px;
    }
  }
`;

const SelectionCriteriaDetails = () => {
  const { t } = useTranslation();
  const [error, setError] = useState<any>(useLocation().state);
  const [, setSelectedGuests] = useState(selectionStore.guests);
  const [, setSelectedChildren] = useState(selectionStore.children);
  const [, setSelectedTime] = useState(selectionStore.time);
  const childrenManagementEnabled = moduleSettings.childrenManagementEnabled;
  const isSelectOnlyPaxFirst = moduleSettings.selectOnlyPaxFirst;
  const isUpdateFromBooking = moduleSettings.isUpdateFromBooking;
  const enableRestaurantSuggestions =
    moduleSettings.enableRestaurantSuggestions;
  const [selectedOnlyPaxDone, setSelectedOnlyPaxDone] = useState(
    !isSelectOnlyPaxFirst || !!selectionStore.guests
  );
  const [selectedPaxDone, setSelectedPaxDone] = useState(
    !!selectionStore.guests
  );
  const [canContinue, setCanContinue] = useState(
    !isUpdateFromBooking &&
      selectedOnlyPaxDone &&
      selectionStore.guests &&
      selectionStore.date &&
      selectionStore.date.isValid() &&
      selectionStore.time
  );
  const throwError = useErrorHandler();
  const [redirect, setRedirect] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldForceAreaSelection, setShouldForceAreaSelection] = useState(
    moduleSettings.forceAreaSelection && !selectionStore.area
  );

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "selection-criteria-all",
      [
        GolEventListener.Guests,
        GolEventListener.CalendarDate,
        GolEventListener.Time,
      ],
      () => {
        setError("");
      }
    );

    return () => {
      selectionStoreChangesService.unregisterListener("selection-criteria-all");
    };
  }, []);

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "selection-criteria-guests",
      [GolEventListener.Guests],
      () => {
        setSelectedGuests(selectionStore.guests);
        setSelectedChildren(selectionStore.children);
        selectionStore.guests > 0 || selectionStore.children > 0
          ? setSelectedPaxDone(true)
          : setSelectedPaxDone(false);
        if (!childrenManagementEnabled && !selectedOnlyPaxDone) {
          setSelectedOnlyPaxDone(true);
        }
      }
    );

    return () => {
      selectionStoreChangesService.unregisterListener(
        "selection-criteria-guests"
      );
    };
  }, []);

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "selection-criteria-time",
      [GolEventListener.Time],
      () => {
        setSelectedTime(selectionStore.time);
        setCanContinue(
          selectedOnlyPaxDone &&
            selectionStore.guests &&
            selectionStore.date &&
            selectionStore.date.isValid() &&
            selectionStore.time
        );
        if (selectionStore.time) {
          initPendingBooking();
        }
      }
    );

    return () => {
      selectionStoreChangesService.unregisterListener(
        "selection-criteria-time"
      );
    };
  }, []);

  useEffect(() => {
    selectionStoreChangesService.registerListener(
      "filter",
      [GolEventListener.Area],
      () => {
        setShouldForceAreaSelection(selectionStore.area === "");
      }
    );

    return () => {
      selectionStoreChangesService.unregisterListener("filter");
    };
  }, []);

  const commitPendingBooking = useCallback(() => {
    const subscription = createInitialPendingBookingRequest().subscribe(
      (resp: PendingBooking) => {
        moduleSettings.initialBookingCreated = true;
        pendingBookingStore.pendingBooking = resp;
        if (resp.available_commercials) {
          selectionStore.selectedOffer = null;
          commercialsStore.offers = resp.available_commercials.filter(
            (commercial) => commercial.object === CommercialKeys.offers
          );
          commercialsStore.events = resp.available_commercials.filter(
            (commercial) => commercial.object === CommercialKeys.events
          );
        } else {
          commercialsStore.offers = [];
          commercialsStore.events = [];
        }
        if (resp.available_menus) {
          commercialsStore.menus = resp.available_menus;
          selectionStore.selectedMenus = resp.available_menus.map(
            (availableMenu: Menu) => {
              return { menuId: availableMenu.id, count: 0 };
            }
          );
        } else {
          commercialsStore.menus = [];
        }
        setIsLoading(false);
        setRedirect(true);
      },
      throwError
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [throwError]);

  const handleSelectedPaxDone = () => {
    setSelectedOnlyPaxDone(true);
    setSelectedPaxDone(true);
  };

  const initPendingBooking = () => {
    setIsLoading(true);
    if (moduleSettings.initialBookingCreated) {
      moduleSettings.initialBookingCreated = false;
      pendingBookingStore.resetBookingState();
      cancelPendingBookingRequest();
    }
    commitPendingBooking();
  };

  useEffect(() => {
    if (
      requestParamsImpl.overwrittenPax() &&
      requestParamsImpl.overwrittenDate() &&
      requestParamsImpl.overwrittenTime() &&
      !selectionStore.backButtonClicked
    ) {
      initPendingBooking();
    }
  }, []);

  return (
    <SelectionCriteriaWrapper data-test="selection-criteria">
      {error && error["errorDetails"] && (
        <ErrorMessage
          error={error["error"]}
          status={
            error["errorDetails"]["code"] ===
            ApiErrorCode.pending_booking_unfound
              ? "warning"
              : "Error"
          }
          data-test="error-message"
        />
      )}
      <Filters
        alignPosition={FiltersPosition.center}
        shouldForceAreaSelection={shouldForceAreaSelection}
      />
      {!shouldForceAreaSelection && (
        <SelectionCriteriaLayout>
          <SelectionPax />
          {(isUpdateFromBooking || selectedOnlyPaxDone) && (
            <SelectionCalendar />
          )}
          {(isUpdateFromBooking || selectedOnlyPaxDone) && <SelectionTime />}
        </SelectionCriteriaLayout>
      )}
      {(canContinue ||
        isUpdateFromBooking ||
        (childrenManagementEnabled &&
          isSelectOnlyPaxFirst &&
          !selectedOnlyPaxDone)) && (
        <ContinueButton
          disabled={childrenManagementEnabled && !selectedPaxDone}
          buttonTextKey={t("button.valid")}
          onClick={
            isUpdateFromBooking || (selectedPaxDone && canContinue)
              ? initPendingBooking
              : handleSelectedPaxDone
          }
        />
      )}
      {isLoading && <Loader position={"fixed"} />}
      {redirect && (
        <Redirect to={buildRouteWithParams(config.routes.commercials)} />
      )}
      {!shouldForceAreaSelection &&
        (!isSelectOnlyPaxFirst ||
          (isSelectOnlyPaxFirst && selectedOnlyPaxDone)) &&
        enableRestaurantSuggestions && <RestaurantGroupSuggestions />}
    </SelectionCriteriaWrapper>
  );
};
export const Selection = memo(SelectionCriteriaDetails);
